import type { ProductSizeOption } from '@repo/api-contracts';
import { type ReactElement, useId } from 'react';
import { Button, cn, FormField } from '@/shared/ui';
import type { SizeQuantityRow } from '../model/size-allocation';

interface DestinationSizeRowsProps {
  readonly options: readonly ProductSizeOption[];
  readonly rows: readonly SizeQuantityRow[];
  readonly error?: string | undefined;
  readonly disabled?: boolean;
  readonly onChange: (rows: readonly SizeQuantityRow[]) => void;
}

export const DestinationSizeRows = ({
  options,
  rows,
  error,
  disabled = false,
  onChange,
}: DestinationSizeRowsProps): ReactElement => {
  const errorId = useId();
  const updateRow = (index: number, patch: Partial<SizeQuantityRow>) => {
    onChange(rows.map((row, rowIndex) => (rowIndex === index ? { ...row, ...patch } : row)));
  };

  return (
    <fieldset
      disabled={disabled}
      aria-label="배송지 사이즈 배분"
      aria-describedby={error ? errorId : undefined}
      className="min-w-0 space-y-3"
    >
      {rows.map((row, index) => (
        // 줄에 영구 id가 없고 사이즈는 중복·미선택일 수 있다. 입력 값은 모두 제어된다.
        // biome-ignore lint/suspicious/noArrayIndexKey: 입력 줄의 위치가 변경·삭제 콜백의 식별자다.
        <div key={index} className="flex items-end gap-3">
          <select
            aria-label={`사이즈 ${index + 1}`}
            value={row.sizeOptionId}
            aria-invalid={error ? true : undefined}
            aria-describedby={error ? errorId : undefined}
            className={cn(
              'h-11 w-28 min-w-0 shrink-0 rounded-full border border-input bg-background px-4 text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring disabled:opacity-50',
              error && 'border-destructive',
            )}
            onChange={(event) => updateRow(index, { sizeOptionId: event.target.value })}
          >
            <option value="">사이즈 선택</option>
            {options.map((option) => (
              <option key={option.id} value={option.id}>
                {option.name}
              </option>
            ))}
          </select>
          <FormField
            label="수량"
            inputMode="numeric"
            autoComplete="off"
            className="h-11 tabular-nums"
            containerClassName="w-28"
            value={row.quantity}
            aria-invalid={error ? true : undefined}
            aria-describedby={error ? errorId : undefined}
            onChange={(event) => updateRow(index, { quantity: event.target.value })}
          />
          <Button
            type="button"
            variant="ghost"
            size="sm"
            aria-label={`사이즈 줄 ${index + 1} 삭제`}
            onClick={() => onChange(rows.filter((_, rowIndex) => rowIndex !== index))}
          >
            삭제
          </Button>
        </div>
      ))}
      <Button
        type="button"
        variant="outline"
        size="sm"
        disabled={rows.length >= options.length}
        onClick={() => onChange([...rows, { sizeOptionId: '', quantity: '' }])}
      >
        + 사이즈 추가
      </Button>
      {error ? (
        <p id={errorId} role="alert" className="text-xs text-destructive">
          {error}
        </p>
      ) : null}
    </fieldset>
  );
};
